import { ActivityDetail } from "@/types/activitydetail";
import axios from 'axios';
import { getSessionId } from '@/lib/sessionId';
// import { BookingFormData, DayAvailability, TimeSlot } from "@/types/bookinghome";
import { Activity, Business } from "@/types/index";
import { MapBounds } from "@/types/homepage";
import { PricingResponse } from "@/types/pricing";
import { format } from 'date-fns';

interface BookingDetails {
  id: string;
  bookingNumber: string;
  status: string;
  date: string;
  time: {
    start: string;
    end: string;
  };
  activity: {
    id: string;
    title: string;  
    description: string;
  };
  business: {
    id: string;
    name: string;
  };
  customer: {
    name: string;
    email: string;
    phone: string;
  };
  payment: {  
    status: string;
    method: string;
    amount: number;
    currency: string;
  };
  participants: Array<{
    type: string;
    quantity: number;
    price: number;
  }>;
  totalParticipants: number;
}


const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export async function fetchBusinessesInBounds(bounds: MapBounds): Promise<Business[]> {
  const boundsString = `${bounds.southWest.longitude},${bounds.southWest.latitude},${bounds.northEast.longitude},${bounds.northEast.latitude}`;
  
  const response = await axios.get(`${BASE_URL}/animal-activities/businesses-in-bounds`, {
    params: { bounds: boundsString }
  });
  
  return response.data;
}


export async function fetchBusinessActivities(businessId: string): Promise<Activity[]> {
  const response = await axios.get(`${BASE_URL}/animal-activities/business/${businessId}`);
  return response.data;
}

export const fetchActivityDetails = async (activityId: string): Promise<ActivityDetail> => {
  if (!activityId) {
    throw new Error('Activity ID is required');
  }
  try {
    const response = await axios.get(`${BASE_URL}/animal-activities/${activityId}`);
    if (!response.data) throw new Error('No data received');
    
    // Transform the business object to ensure _id is available
    if (response.data.business) {
      response.data.business = {
        ...response.data.business,
        _id: response.data.business._id || response.data.business.id // Try both _id and id
      };
      
      // Log for debugging
      console.log('Business data after transformation:', response.data.business);
    }
  
    return response.data;
  } catch (error) {
    console.error('Error fetching activity details:', error);
    throw error;
  }
};


// Get pricing for different participant types
export const getActivityPricing = async (activityId: string): Promise<PricingResponse> => {
  const response = await axios.get(`${BASE_URL}/activity-slots/${activityId}/pricing`);
  return response.data;
};

// Update the existing getActivitySlots to match backend response
export const getActivitySlots = async (
  activityId: string,
  date: string
): Promise<{
  slots: {
    id: string;
    startTime: string;
    endTime: string;
    available: boolean;
    price: {
      amount: number;
      currency: string;
    };
    maxParticipants: number;
    currentBookings: number;
  }[];
}> => {
  const response = await axios.get(
    `${BASE_URL}/activity-slots/get-activity-slots-one-day/${activityId}`,
    { params: { date } }
  );
  return response.data;
};

// Update availability endpoint to match backend
export const getActivityAvailability = async (
  activityId: string,
  startDate: string,
  endDate: string
): Promise<{
  date: string;
  availableSpots: number;
  isBookable: boolean;
}[]> => {
  if (process.env.NODE_ENV === 'development') {
    console.log('🌐 [API DEBUG] Making availability request:', {
      url: `${BASE_URL}/activity-slots/get-activity-slots-range/${activityId}/availability`,
      params: { startDate, endDate }
    });
  }
  
  const response = await axios.get(
    `${BASE_URL}/activity-slots/get-activity-slots-range/${activityId}/availability`,
    { params: { startDate, endDate } }
  );
  
  if (process.env.NODE_ENV === 'development') {
    console.log('📝 [API DEBUG] Raw availability response:', {
      status: response.status,
      dataLength: Array.isArray(response.data?.data) ? response.data.data.length : 'N/A'
    });
  }
  
  // Handle different response structures
  if (response.data && Array.isArray(response.data.data)) {
    return response.data.data;
  } else if (Array.isArray(response.data)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️ [API DEBUG] Response data is directly an array, using it');
    }
    return response.data;
  } else {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ [API DEBUG] Unexpected response structure:', response.data);
    }
    return [];
  }
};

export const createBooking = async (bookingData: {
  activityId: string;
  selectedDate: Date;
  selectedTimeSlot: string;
  participants: {
    adult: number;
    child: number;
    student: number;
  };
  customerDetails: {
    name: string;
    email: string;
    phone?: string;
  };
  specialRequests?: string;
}) => {
  try {
    // Format the date using the date-fns format function to preserve the selected date
    const formattedDate = format(bookingData.selectedDate, 'yyyy-MM-dd');
    
    const formattedBookingData = {
      activityId: bookingData.activityId,
      date: formattedDate,
      slotTime: bookingData.selectedTimeSlot,
      participants: bookingData.participants,
      customerDetails: bookingData.customerDetails,
      specialRequests: bookingData.specialRequests,
      sessionId: getSessionId(),
    };

    console.log('Sending booking data:', formattedBookingData);
    const response = await axios.post(`${BASE_URL}/bookings`, formattedBookingData);
    return response.data;
  } catch (error: any) {
    console.error('Booking error:', error.response?.data || error.message);
    throw error;
  }
};


export const processPayment = async (paymentData: {
  bookingId: string;
  amount: number;
  billingDetails: {
    name: string;
    email: string;
    address: string;
  };
}) => {
  const response = await axios.post(`${BASE_URL}/payments`, paymentData);
  return response.data;
};

export const initiatePayment = async (paymentData: {
  bookingId: string;
  amount: number;
  paymentMethod: 'stripe'
}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/payments/initiate`, // Using BASE_URL constant
      paymentData
    );
    return response.data;
  } catch (error: any) {
    console.error('Payment initiation error:', error.response?.data || error.message);
    throw error;
  }
};

export const initiateCustomAdventurePayment = async (paymentData: {
  requestNumber: string;
  amount: number;
  paymentMethod: 'stripe'
}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/custom-adventure-bookings/payment/initiate`,
      paymentData
    );
    return response.data;
  } catch (error: any) {
    console.error('Custom adventure payment initiation error:', error.response?.data || error.message);
    throw error;
  }
};

export const getBookingDetails = async (bookingId: string): Promise<BookingDetails> => {
  try {
    const response = await axios.get(`${BASE_URL}/bookings/${bookingId}`);
    console.log('API Response:', response.data); // Add this log
    
    // Validate the response data
    if (!response.data) {
      throw new Error('No data received from API');
    }

    return response.data;
  } catch (error) {
    console.error('Error in getBookingDetails:', error);
    throw error;
  }
};